/* MOMAX Hero: simplified Swiper without scroll/reveal */
(() => {
  if (customElements.get('mm-hero')) return;
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px)');

  class MmHero extends HTMLElement {
    connectedCallback() {
      this._generation = (this._generation || 0) + 1;
      const generation = this._generation;
      queueMicrotask(() => {
        if (this.isConnected && generation === this._generation) this._connect();
      });
    }

    _connect() {
      this._events = new AbortController();
      this._shell = this.querySelector('.mm-hero__shell');
      this._slides = [...this.querySelectorAll('.mm-hero__slide')];
      this._cards = [...this.querySelectorAll('.mm-hero__card-slide')];
      this._tablist = this.querySelector('.mm-hero__tabs');
      this._group = this.querySelector('.mm-hero__card-group');
      this._index = 0;
      this._pendingCard = 0;

      if (this._group) this._group.hidden = this._cards.length === 0;

      this._buildControls();

      this._listen(this._tablist, 'click', event => {
        const tab = event.target.closest('[data-mm-hero-tab]');
        if (tab) this._goTo(Number(tab.dataset.index));
      });
      this._listen(this._tablist, 'keydown', event => this._tabKey(event));

      this._listen(this, 'shopify:block:select', event => {
        const index = this._slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId);
        if (index >= 0) this._goTo(index);
        const cardIndex = this._cards.findIndex(card => card.dataset.blockId === event.detail?.blockId);
        if (cardIndex >= 0) {
          this._pendingCard = cardIndex;
          this._cardSwiper?.slideTo(cardIndex, 0);
        }
      });

      this._listen(window, 'resize', () => this._layout());
      this._listen(desktop, 'change', () => {
        this._touch = null;
        if (this._mainSwiper) {
          const targetDirection = desktop.matches ? 'vertical' : 'horizontal';
          if (this._mainSwiper.params.direction !== targetDirection) {
            this._mainSwiper.changeDirection(targetDirection, true);
          }
        }
      });
      this._listen(motion, 'change', () => {
        if (this._mainSwiper) this._mainSwiper.params.speed = motion.matches ? 0 : 650;
        if (this._cardSwiper) this._cardSwiper.params.speed = motion.matches ? 0 : 400;
      });

      this._resizeObserver = new ResizeObserver(() => this._layout());
      this._resizeObserver.observe(this._shell);
      this.querySelectorAll('.mm-hero__bottom-right-content, .mm-hero__brand').forEach(el => this._resizeObserver.observe(el));

      this._sync(0);
      this._syncCards(0);
      this._layout();

      let tries = 0;
      const initialize = () => {
        if (!this.isConnected) return;
        if (window.Swiper) {
          this._initSwipers();
        } else if (++tries < 100) {
          this._libraryTimer = setTimeout(initialize, 50);
        } else {
          this._tablist.hidden = true;
          console.warn('[mm-hero] Swiper core could not load.');
        }
      };
      initialize();
    }

    _listen(target, name, handler, options = {}) {
      target?.addEventListener(name, handler, { ...options, signal: this._events.signal });
    }

    _buildControls() {
      const hasStaticTabs = this._tablist.querySelector('.mm-hero__tab-btn');
      if (hasStaticTabs) {
        this._tabs = [...this._tablist.querySelectorAll('.mm-hero__tab-btn')];
        this._tablist.hidden = false;
        this._tablist.style.setProperty('--mm-hero-tab-count', Math.max(1, this._tabs.length));
      } else {
        this._tablist.replaceChildren();
        this._tabs = this._slides.map((slide, index) => {
        const tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'mm-hero__tab-btn';
        tab.dataset.mmHeroTab = '';
        tab.dataset.index = index;
        tab.id = `${slide.id}-tab`;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', slide.id);
        slide.setAttribute('aria-labelledby', tab.id);
        for (const [key, className] of [['tabTitle', 'mm-hero__tab-value'], ['tabUnit', 'mm-hero__tab-unit']]) {
          const span = document.createElement('span');
          span.className = className;
          span.textContent = slide.dataset[key];
          tab.append(span);
        }
        this._tablist.append(tab);
        return tab;
      });
      this._tablist.hidden = !this._slides.length;
      this._tablist.style.setProperty('--mm-hero-tab-count', Math.max(1, this._slides.length));
      }

      const dots = this.querySelector('.mm-hero__card-dots');
      this._dots = [];
      this._prev = this._next = null;
      if (!dots) return;
      dots.replaceChildren();
      this._dots = this._cards.map((card, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'mm-hero__card-dot';
        dot.setAttribute('aria-label', `${dots.dataset.cardLabel} ${index + 1}`);
        this._listen(dot, 'click', () => this._cardSwiper?.slideTo(index));
        dots.append(dot);
        return dot;
      });
      this._prev = this.querySelector('[data-mm-hero-card-prev]');
      this._next = this.querySelector('[data-mm-hero-card-next]');
      this._listen(this._prev, 'click', () => this._cardSwiper?.slidePrev());
      this._listen(this._next, 'click', () => this._cardSwiper?.slideNext());
      this.querySelector('.mm-hero__card-nav').hidden = this._cards.length <= 1;
    }

    _initSwipers() {
      if (this._slides.length) {
        this._mainSwiper = new window.Swiper(this.querySelector('.mm-hero__swiper'), {
          direction: desktop.matches ? 'vertical' : 'horizontal',
          breakpoints: {
            990: {
              direction: 'vertical',
              allowTouchMove: false
            }
          },
          slidesPerView: 1,
          speed: motion.matches ? 0 : 650,
          allowTouchMove: !desktop.matches,
          initialSlide: this._index,
          on: {
            slideChange: swiper => {
              this._sync(swiper.activeIndex);
            }
          }
        });
      }
      if (this._cards.length) {
        this._cardSwiper = new window.Swiper(this.querySelector('.mm-hero__card-swiper'), {
          direction: 'horizontal',
          slidesPerView: 1,
          speed: motion.matches ? 0 : 400,
          allowTouchMove: true,
          initialSlide: this._pendingCard || 0,
          on: { slideChange: swiper => this._syncCards(swiper.activeIndex) }
        });
      }
      this._sync(this._index);
      this._syncCards(this._cardSwiper?.activeIndex || 0);
    }

    _sync(index) {
      this._index = index;
      this._slides.forEach((slide, i) => {
        const active = i === index;
        slide.setAttribute('aria-hidden', String(!active));
        slide.classList.toggle('is-current', active);
      });
      this._tabs.forEach((tab, i) => {
        tab.classList.toggle('is-current', i === index);
        tab.setAttribute('aria-selected', String(i === index));
      });
      this._tablist.setAttribute('aria-selectedindex', index);
    }

    _syncCards(index) {
      this._cards.forEach((card, i) => {
        card.setAttribute('aria-hidden', String(i !== index));
        card.classList.toggle('is-current', i === index);
      });
      this._dots?.forEach((dot, i) => {
        dot.classList.toggle('is-current', i === index);
        dot.setAttribute('aria-selected', String(i === index));
      });
    }

    _goTo(index) {
      if (index < 0 || index >= this._slides.length) return;
      this._mainSwiper?.slideTo(index, motion.matches ? 0 : 650);
      this._tabs[index]?.focus({ preventScroll: true });
    }

    _tabKey(event) {
      const tab = event.target.closest('[data-mm-hero-tab]');
      if (!tab) return;
      const index = Number(tab.dataset.index);
      let next = index;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        next = Math.min(index + 1, this._slides.length - 1);
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        next = Math.max(index - 1, 0);
      } else if (event.key === 'Home') {
        next = 0;
      } else if (event.key === 'End') {
        next = this._slides.length - 1;
      } else {
        return;
      }
      event.preventDefault();
      if (next !== index) this._goTo(next);
      this._tabs[next]?.focus({ preventScroll: true });
    }

    _layout() {
      if (!this._shell) return;
      const desktop = window.matchMedia('(min-width: 990px)').matches;
      const ctaTop = this.querySelector('.mm-hero__cta')?.getBoundingClientRect()?.top ?? 0;
      const root = document.documentElement;
      root.style.setProperty('--mm-hero-cta-top', `${ctaTop}px`);
      this._shell.style.setProperty('--mm-hero-shell-h', desktop ? `${ctaTop + 76}px` : 'auto');
      const bottomRight = this.querySelector('.mm-hero__bottom-right-content');
      if (bottomRight) bottomRight.style.maxWidth = `${Math.min(this._shell.offsetWidth * 0.5, 520)}px`;
    }

    disconnectedCallback() {
      this._events?.abort();
      this._resizeObserver?.disconnect();
      clearTimeout(this._libraryTimer);
      this._mainSwiper?.destroy();
      this._cardSwiper?.destroy();
    }
  }

  customElements.define('mm-hero', MmHero);
})();
